import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Divider,
  Grid,
  Paper,
} from "@mui/material";
import { Delete as DeleteIcon, ContentCopy as CopyIcon } from "@mui/icons-material";
import {
  updateElement,
  deleteSelectedElements,
  copyToClipboard,
} from "../../../store/warehouseSlice";

const PropertiesPanel = () => {
  const dispatch = useDispatch();
  const { elements, selectedElements } = useSelector((state) => state.warehouse);

  if (selectedElements.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="h6" mb={1}>
          Keine Auswahl
        </Typography>
        <Typography variant="body2">
          Wählen Sie ein Element auf der Leinwand aus, um seine Eigenschaften zu bearbeiten
        </Typography>
      </Box>
    );
  }

  if (selectedElements.length > 1) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" mb={2}>
          Mehrere ausgewählt ({selectedElements.length})
        </Typography>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<CopyIcon />}
          onClick={() => dispatch(copyToClipboard())}
          sx={{ mb: 1 }}
        >
          Auswahl kopieren
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => dispatch(deleteSelectedElements())}
        >
          Auswahl löschen
        </Button>
      </Box>
    );
  }

  const selectedElement = elements.find((e) => e.id === selectedElements[0]);
  if (!selectedElement) return null;

  const handleUpdate = (field, value) => {
    dispatch(updateElement({ id: selectedElement.id, [field]: value }));
  };

  const handlePositionUpdate = (axis, value) => {
    dispatch(
      updateElement({
        id: selectedElement.id,
        gridPosition: {
          ...selectedElement.gridPosition,
          [axis]: Number(value),
        },
      })
    );
  };

  const renderRackProperties = () => (
    <>
      <TextField
        fullWidth
        select
        label="Regaltyp"
        value={selectedElement.rackType || "shelf"}
        onChange={(e) => handleUpdate("rackType", e.target.value)}
        margin="normal"
      >
        <MenuItem value="shelf">Regalfach</MenuItem>
        <MenuItem value="pallet">Palettenregal</MenuItem>
        <MenuItem value="bin">Behälterregal</MenuItem>
        <MenuItem value="floor">Bodenlagerung</MenuItem>
      </TextField>
      <TextField
        fullWidth
        type="number"
        label="Anzahl der Fachböden"
        value={selectedElement.numberOfShelves || 3}
        onChange={(e) => handleUpdate("numberOfShelves", Number(e.target.value))}
        margin="normal"
        inputProps={{ min: 1, max: 10 }}
      />
      <TextField
        fullWidth
        select
        label="Drehung"
        value={selectedElement.rotation || 0}
        onChange={(e) => handleUpdate("rotation", Number(e.target.value))}
        margin="normal"
      >
        <MenuItem value={0}>0° (Horizontal)</MenuItem>
        <MenuItem value={90}>90° (Vertikal)</MenuItem>
        <MenuItem value={180}>180°</MenuItem>
        <MenuItem value={270}>270°</MenuItem>
      </TextField>
    </>
  );

  const renderZoneProperties = () => (
    <>
      <TextField
        fullWidth
        select
        label="Zonentyp"
        value={selectedElement.zoneType || "storage"}
        onChange={(e) => handleUpdate("zoneType", e.target.value)}
        margin="normal"
      >
        <MenuItem value="storage">Lagerung</MenuItem>
        <MenuItem value="receiving">Wareneingang</MenuItem>
        <MenuItem value="shipping">Versand</MenuItem>
        <MenuItem value="work_area">Arbeitsbereich</MenuItem>
        <MenuItem value="restricted">Eingeschränkt</MenuItem>
      </TextField>
      <TextField
        fullWidth
        type="color"
        label="Zonenfarbe"
        value={selectedElement.color || "#2ecc71"}
        onChange={(e) => handleUpdate("color", e.target.value)}
        margin="normal"
      />
    </>
  );

  return (
    <Box sx={{ p: 3, height: "100%", overflowY: "auto" }}>
      <Typography variant="h6" fontWeight="600" mb={2}>
        Eigenschaften
      </Typography>

      <Paper elevation={0} sx={{ p: 2, backgroundColor: "#f8f9fa", mb: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          Elementtyp
        </Typography>
        <Typography variant="body1" fontWeight="600" textTransform="capitalize">
          {selectedElement.type === "rack" ? "Regal" :
           selectedElement.type === "zone" ? "Zone" :
           selectedElement.type === "wall" ? "Wand" :
           selectedElement.type === "door" ? "Tür" :
           selectedElement.type === "aisle" ? "Gang" :
           selectedElement.type === "column" ? "Säule" :
           selectedElement.type}
        </Typography>
      </Paper>

      <Divider sx={{ mb: 2 }} />

      {/* Basic Properties */}
      <Typography variant="subtitle2" fontWeight="600" mb={1.5}>
        Grundinformationen
      </Typography>
      <TextField
        fullWidth
        label="Name"
        value={selectedElement.name || ""}
        onChange={(e) => handleUpdate("name", e.target.value)}
        margin="normal"
      />
      <TextField
        fullWidth
        label="Code"
        value={selectedElement.code || ""}
        onChange={(e) => handleUpdate("code", e.target.value)}
        margin="normal"
        helperText="Eindeutige Kennung für dieses Element"
      />

      <Divider sx={{ my: 2 }} />

      {/* Position & Size */}
      <Typography variant="subtitle2" fontWeight="600" mb={1.5}>
        Position & Größe
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="X Position"
            value={selectedElement.gridPosition?.x || 0}
            onChange={(e) => handlePositionUpdate("x", e.target.value)}
            inputProps={{ min: 0, step: 1 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="Y Position"
            value={selectedElement.gridPosition?.y || 0}
            onChange={(e) => handlePositionUpdate("y", e.target.value)}
            inputProps={{ min: 0, step: 1 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="Breite"
            value={selectedElement.gridPosition?.width || 1}
            onChange={(e) => handlePositionUpdate("width", e.target.value)}
            inputProps={{ min: 1, step: 1 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="Höhe"
            value={selectedElement.gridPosition?.height || 1}
            onChange={(e) => handlePositionUpdate("height", e.target.value)}
            inputProps={{ min: 1, step: 1 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Type-Specific Properties */}
      <Typography variant="subtitle2" fontWeight="600" mb={1.5}>
        Typspezifische Einstellungen
      </Typography>
      {selectedElement.type === "rack" && renderRackProperties()}
      {selectedElement.type === "zone" && renderZoneProperties()}

      <Divider sx={{ my: 3 }} />

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<CopyIcon />}
          onClick={() => dispatch(copyToClipboard())}
        >
          Kopieren
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => dispatch(deleteSelectedElements())}
        >
          Löschen
        </Button>
      </Box>
    </Box>
  );
};

export default PropertiesPanel;
